// ── Clientes do Hub (/clientes, aba "Clientes (Hub)") ─────────────
// Núcleo puro: nada aqui conhece Supabase. Espelha TODAS as empresas do
// BlueHub (lead, ativa, inativa, cancelada), copiadas para o CRM por um job
// — não é a fila de Implantação (`csModel.situacaoImplantacao`), que só
// lista quem o comercial liberou.
//
// O vínculo com a empresa do CRM é SUGERIDO por CNPJ e CONFIRMADO por uma
// pessoa (owner ou CS) — ver `cs.vincular_empresa_hub`/`desvincular_empresa_hub`.
// Nenhuma regra de vínculo mora aqui: o núcleo só filtra o que já veio pronto
// do banco.

import { normalizeText, onlyDigits } from './csModel';
import type { ContratoStatus, ImportacaoStatus } from './csModel';

// ── Status e modelo ────────────────────────────────────────────────

/**
 * Os valores que o Hub manda hoje. O campo de verdade (`ClienteHub.status`)
 * é `string`: um status novo do Hub não pode travar a tela, só aparecer sem
 * tradução — ver `rotuloStatusHub`.
 */
export type StatusHubConhecido = 'pending_payment' | 'active' | 'inactive' | 'canceled';

export const STATUS_HUB_LABELS: Record<StatusHubConhecido, string> = {
  active:          'Ativo',
  pending_payment: 'Aguardando pagamento',
  inactive:        'Inativo',
  canceled:        'Cancelado',
};

/** Texto cru para status que o Hub ainda não tinha quando este mapa foi escrito. */
export const rotuloStatusHub = (status: string): string =>
  STATUS_HUB_LABELS[status as StatusHubConhecido] ?? status;

/** O que a empresa é para o Hub — mesma modalidade de `csModel.ContratoModalidade`,
 *  mas com o nome de coluna do Hub (`folha`, não `desconto_folha`). */
export type ModeloHub = 'folha' | 'convenio' | 'parceira';

export const MODELO_HUB_LABELS: Record<ModeloHub, string> = {
  folha:    'Desconto em folha',
  convenio: 'Conveniada',
  parceira: 'Empresa parceira',
};

/** `null` é estado normal: lead do Hub que ainda não definiu o modelo. */
export const rotuloModeloHub = (modelo: ModeloHub | string | null): string => {
  if (modelo == null) return 'Não classificado';
  return MODELO_HUB_LABELS[modelo as ModeloHub] ?? modelo;
};

/** Padrão dos chips de status na tela: esconde lead e cancelada por padrão. */
export const STATUS_PADRAO: StatusHubConhecido[] = ['active', 'inactive'];

// ── Linha do espelho ───────────────────────────────────────────────

export interface RepresentanteHub {
  publicId: string;
  nome: string;
  email: string;
  papel: string;
}

export interface GrupoHub {
  publicId: string;
  nome: string;
  papel: string;
  /** Empresa principal do grupo, quando esta linha é uma filial. */
  principalPublicId: string | null;
}

/** Uma empresa do CRM candidata ao vínculo — por CNPJ igual (matriz ou
 *  filial) ou por grupo do Hub. `como` é o papel que ESTA empresa do Hub
 *  assumiria ao vincular: `matriz` (o CNPJ bate com `empresas.cnpj`, ou o
 *  grupo do Hub aponta esta como a principal) ou `filial` (bate com um CNPJ
 *  de `empresa_filiais`, ou o grupo do Hub aponta outra como principal). */
export interface SugestaoVinculoHub {
  empresaId: string;
  nome: string;
  cnpj: string | null;
  workspaceId: string | null;
  como: 'matriz' | 'filial';
}

/** Uma empresa do CRM encontrada na busca manual (`cs.buscar_empresas_para_vinculo`)
 *  — para o caso que não tem sugestão por CNPJ nem por grupo (ex.: cliente
 *  sem CNPJ cadastrado). `temContaMatriz` decide qual botão a tela oferece:
 *  sem conta-matriz na marca, só "vincular como matriz"; com ela, só
 *  "vincular como filial" — a matriz sempre vem primeiro (ver §5.1 do plano). */
export interface EmpresaParaVinculo {
  empresaId: string;
  nome: string;
  cnpj: string | null;
  workspaceId: string | null;
  temContaMatriz: boolean;
  filiais: number;
}

/** Uma conta do Hub vinculada à empresa (`cs.contas_hub_da_empresa`) — uma
 *  linha por CNPJ do grupo (a matriz E cada filial), com o retrato daquele
 *  CNPJ no Hub. Quem decide quem vê é a RPC (owner, CS que lê o workspace,
 *  ou Implantação com a implantação liberada). */
export interface ContaHubDaEmpresa {
  hubPublicId: string;
  marca: string;
  /** `null` = esta linha é a matriz (a própria empresa); preenchido = o id
   *  da filial em `public.empresa_filiais`. */
  empresaCnpjId: string | null;
  papelCrm: 'matriz' | 'filial' | null;
  grupoPublicId: string | null;
  papelHub: 'principal' | 'filial' | null;
  razaoSocial: string;
  cnpj: string | null;
  status: string;
  modelo: ModeloHub | null;
  vidasAtivas: number;
  vidasVendidas: number;
  representante: RepresentanteHub | null;
  sincronizadoEm: string | null;
  ausenteDesde: string | null;
}

/** Uma linha de `cs.listar_clientes_hub()`. `empresaId` nulo = sem vínculo
 *  confirmado com o CRM — os campos que só existem no CRM (contrato, vidas
 *  cadastradas, implantação) vêm nulos/zerados nesse caso.
 *
 *  Só os campos que TODA linha tem (identidade, status, vidas do Hub,
 *  vínculo e sugestões) são obrigatórios. O resto é opcional de propósito:
 *  é o que permite um teste (ou uma tela) montar uma linha mínima sem
 *  repetir 15 campos que não interessam ao caso testado. */
export interface ClienteHub {
  hubPublicId: string;
  marca?: string;
  razaoSocial: string;
  cnpj: string | null;
  status: string;
  modelo: ModeloHub | null;
  vidasAtivas: number;
  vidasVendidas: number;
  representante?: RepresentanteHub | null;
  dataAtivacao?: string | null;
  /** Grupo (filiais) do Hub — não confundir com o grupo de empresas do CRM. */
  grupo?: GrupoHub | null;
  sincronizadoEm?: string | null;
  /** Não nulo = a empresa não veio na última leitura do Hub. */
  ausenteDesde?: string | null;

  // ── Vínculo confirmado com o CRM ──────────────────────────────────
  empresaId: string | null;
  empresaNome: string | null;
  /** `null` = a matriz (ou sem vínculo); preenchido = o id da filial em
   *  `public.empresa_filiais` que este CNPJ do Hub ocupa. */
  empresaCnpjId?: string | null;
  /** Papel desta linha no cliente do CRM — `null` sem vínculo confirmado. */
  papelCrm?: 'matriz' | 'filial' | null;
  /** Nome da empresa do CRM quando esta linha é `filial` — para o rótulo
   *  "Filial de X" na lista. `null` quando a pessoa não lê a empresa (mesma
   *  régua de `empresaNome`). */
  matrizNome?: string | null;
  implantacaoLiberada?: boolean;
  contratoStatus?: ContratoStatus | null;
  dataVenda?: string | null;
  vidasContratadas?: number;
  valorMensalCents?: number;
  vidasCadastradas?: number;
  ultimaImportacaoStatus?: ImportacaoStatus | null;
  ultimaImportacaoEm?: string | null;

  /** Candidatas a vínculo por CNPJ igual — só faz sentido sem `empresaId`. */
  sugestoes: SugestaoVinculoHub[];
}

// ── Filtro (local, lista curta) ────────────────────────────────────

export type VinculoFiltro = 'todos' | 'vinculados' | 'sem_vinculo';

export interface FiltrarClientesHubOptions {
  /** Casa por razão social, nome do CRM (sem acento/caixa) ou dígitos do CNPJ. */
  busca?: string;
  /** Vazio = sem filtro. */
  status?: string[];
  /** Vazio = sem filtro. Aceita `null` para "Não classificado". */
  modelo?: (ModeloHub | null)[];
  vinculo?: VinculoFiltro;
}

export const filtrarClientesHub = (
  lista: ClienteHub[],
  opcoes: FiltrarClientesHubOptions = {},
): ClienteHub[] => {
  const termo = normalizeText(opcoes.busca?.trim() || '');
  const digitosBusca = onlyDigits(opcoes.busca || '');
  const statusFiltro = opcoes.status?.length ? new Set(opcoes.status) : null;
  const modeloFiltro = opcoes.modelo?.length ? opcoes.modelo : null;
  const vinculo = opcoes.vinculo ?? 'todos';

  return lista.filter((cliente) => {
    if (statusFiltro && !statusFiltro.has(cliente.status)) return false;
    if (modeloFiltro && !modeloFiltro.includes(cliente.modelo)) return false;
    if (vinculo === 'vinculados' && !cliente.empresaId) return false;
    if (vinculo === 'sem_vinculo' && cliente.empresaId) return false;

    if (termo || digitosBusca) {
      const bateNome = termo && (
        normalizeText(cliente.razaoSocial).includes(termo)
        || normalizeText(cliente.empresaNome || '').includes(termo)
      );
      const bateCnpj = !!digitosBusca && onlyDigits(cliente.cnpj || '').includes(digitosBusca);
      if (!bateNome && !bateCnpj) return false;
    }
    return true;
  });
};
